"use client";
import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import parse from 'html-react-parser';
import classNames from 'classnames';
import Button from '../../components/Button';
import TextField from '../../components/TextField';
import LoadingSpinner from '../../components/LoadingSpinner';
import Select from '../../components/Select';
import RadioGroup from '../../components/RadioGroup';
import s from './JobDetails.module.scss';
import AttachmentPinIcon from './Attachment-pin.svg';

export interface JobDetailsProps {
  jobId: string;
  title?: string;
  location?: string;
  employmentType?: string;
  description?: string;
  questions?: Array<{
    label?: string;
    required?: boolean;
    fields?: Array<{
      name: string;
      type: string;
      values?: Array<string>;
    }>;
  }>;
  educationRequired?: boolean;
}

interface Education {
  value: string | number | undefined;
  error?: boolean;
}

interface FormData {
  [key: string]: any;
}

const JobDetails: React.FC<JobDetailsProps> = ({
  jobId,
  title = '',
  location = '',
  employmentType = '',
  description = '',
  questions = [],
  educationRequired = false,
}) => {
  const GREENHOUSE_BASE_URL = process.env.NEXT_PUBLIC_GREENHOUSE_BASE_URL;
  
  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<FormData>({});
  const [formErrors, setFormErrors] = useState<FormData>({});
  const [educations, setEducations] = useState<Education[]>([{ value: undefined }]);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [requiredFields, setRequiredFields] = useState<FormData>({});
  const [degrees, setDegrees] = useState<Array<{ name: string; value: string }>>([]);

  // get base64 encoded string from file.
  const getBase64 = (file: File, cb: (result: string) => void) => {
    const reader = new FileReader();
    reader.onload = () => {
      cb(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  // handles textfield onChange event.
  const onTextInputChange = (value: string, key: string) => {
    setErrorMessage('');
    setFormErrors({ ...formErrors, [key]: false });
    setFormData({ ...formData, [key]: value });
  };

  // handles textfield onBlur event.
  const onBlur = (value: string, key: string, isRequired: boolean) => {
    if (isRequired && !value) {
      setFormErrors({ ...formErrors, [key]: true });
    }
  };

  // handles education dropdown onChange event.
  const onEducationValueChange = (value: string, index: number) => {
    setErrorMessage('');
    educations[index].error = false;
    educations[index].value = value;
    setEducations([...educations]);
  };

  // handles resume upload button click event.
  const handleResumeSubmit = () => {
    hiddenFileInput.current?.click();
  };

  // handles hidden input onChange event for resume file.
  const handleHiddenInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrorMessage('');
    const fileUploaded = e.target.files?.[0];
    if (fileUploaded !== undefined) {
      setFormErrors({ ...formErrors, resume: false });
      getBase64(fileUploaded, (result) => {
        setFormData({
          ...formData,
          resume_content: result.substring(28),
          resume_content_filename: fileUploaded.name,
        });
      });
    }
  };

  // returns input field based on question type.
  const getInputField = (question: NonNullable<JobDetailsProps['questions']>[number]) => {
    // skip questions with field name from common questions array.
    const commonQuestions = ['first_name', 'last_name', 'email', 'phone', 'resume', 'cover_letter'];
    if (question?.fields?.[0]?.name && commonQuestions.includes(question.fields[0].name)) {
      return null;
    }

    const inputType = question?.fields?.[0]?.type;
    const fieldName = question?.fields?.[0]?.name ?? '';

    switch (inputType) {
      case 'input_text':
      case 'textarea': {
        return (
          <TextField
            dataTestId="CustomQuestionTextField"
            key={question?.label}
            label={question?.label}
            isRequired={question?.required}
            value={formData[fieldName] || ''}
            onChange={(value) => onTextInputChange(value, fieldName)}
            onBlur={(e) => onBlur(e.target.value, fieldName, question?.required!)}
            helperText={question?.required ? 'Required*' : ''}
            isError={formErrors[fieldName]}
            isTextArea={inputType === 'textarea'}
          />
        );
      }
      case 'multi_value_single_select': {
        return (
          <RadioGroup
            dataTestId="CustomQuestionRadioGroup"
            key={question?.label}
            label={question?.label}
            items={question?.fields?.[0]?.values || []}
            name={fieldName}
            value={formData[fieldName] || ''}
            isRequired={question?.required}
            helperText="Required*"
            isError={formErrors[fieldName]}
            onChange={(value) => onTextInputChange(value, fieldName)}
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  // handles form submit process.
  const onFormSubmit = async () => {
    try {
      // Create a copy of formErrors to avoid direct mutation
      const updatedFormErrors = { ...formErrors };
      let hasErrors = false;
      
      // set error if any required field is not filled.
      questions.forEach((question) => {
        const fieldName = question.fields?.[0]?.name;
        if (
          question.required &&
          fieldName &&
          ((question.fields?.[0]?.type === 'input_file' &&
            fieldName === 'resume' && 
            !formData.resume_content) ||
            (question.fields?.[0]?.type !== 'input_file' && !formData[fieldName]))
        ) {
          updatedFormErrors[fieldName] = true;
          hasErrors = true;
        }
      });

      // Update formErrors state with the new errors
      setFormErrors(updatedFormErrors);
      
      let educationHasErrors = false;
      if (educationRequired && educations.length > 0) {
        // set error if any degree dropdown is remaining.
        const tempEducations = [...educations];
        educations.forEach((education, index) => {
          if (!education?.value) {
            tempEducations[index].error = true;
            educationHasErrors = true;
          }
        });
        setEducations(tempEducations);
      }

      // if any required field is not filled, show error message and return.
      if (
        hasErrors || 
        Object.values(updatedFormErrors).some((error) => error === true) ||
        educationHasErrors
      ) {
        setErrorMessage('Please provide required fields*');
        return;
      }

      // set education data in formData.
      const updatedFormData = {
        ...formData
      };
      
      // Only add education data if we have valid education entries
      if (educationRequired && educations.length > 0 && educations.some(edu => edu.value)) {
        updatedFormData.educations = educations
          .filter(edu => edu.value) // Only include educations with values
          .map((obj) => ({ degree_id: obj.value }));
      }

      // Check if the Greenhouse URL is available
      if (!GREENHOUSE_BASE_URL) {
        setErrorMessage('Unable to submit application due to missing configuration. Please contact support.');
        setIsFormSubmitting(false);
        return;
      }
      
      setIsFormSubmitting(true);

      // Format the data according to Greenhouse API requirements
      const formattedData = {
        ...updatedFormData,
        // If resume file exists, rename the fields to match Greenhouse API requirements
        ...(updatedFormData.resume_content && {
          resume_content: updatedFormData.resume_content,
          resume_content_filename: updatedFormData.resume_content_filename
        })
      };
      
      // Call our Next.js API endpoint which will handle the Greenhouse API call server-side
      fetch(`/api/greenhouse-submit?jobId=${jobId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(formattedData)
      })
        .then(async (response) => {
          if (response.ok) {
            // Clear the form on successful submission
            setFormData({});
            setFormErrors({});
            setEducations([{ value: undefined }]);
            setSuccessMessage('Your application has been submitted successfully.');
            
            // Reset the file input
            if (hiddenFileInput.current) {
              hiddenFileInput.current.value = '';
            }
          } else {
            // Handle error response
            const errorData = await response.json().catch(() => ({}));
            setErrorMessage('Failed to submit application. Please try again later.');
          }
          return response;
        })
        .catch((error) => {
          setErrorMessage('Failed to submit application. Please try again later.');
        });
    
    } catch (error) {
      setErrorMessage('Something went wrong while processing your application. Please try again later.');
    }
    setIsFormSubmitting(false);
  };

  // fetch education degrees from greenhouse.
  useEffect(() => {
    if (educationRequired) {
      fetch(`${GREENHOUSE_BASE_URL}education/degrees`)
        .then((res) => res.json())
        .then((res) => {
          if (res?.items) {
            const tempDegrees = res?.items.map((degree: { text: string; id: string }) => ({
              name: degree.text,
              value: degree.id,
            }));
            setDegrees(tempDegrees);
          } else {
            setDegrees([]);
          }
        })
        .catch(() => {
          setDegrees([]);
        });
    }
  }, [educationRequired, GREENHOUSE_BASE_URL]);

  // get required fields from questions array.
  useEffect(() => {
    const tempObject: FormData = {};
    if (questions?.length > 0) {
      questions.forEach((obj) => {
        const fieldName = obj.fields?.[0]?.name;
        if (fieldName) {
          tempObject[fieldName] = obj?.required ?? false;
        }
      });
      setRequiredFields(tempObject);
    }
  }, [questions]);

  // Show loading spinner while API is processing.
  if (!title) {
    return (
      <div className={s.loaderContainer} data-testid="loadingSpinnerContainer">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className={s.pageContainer} data-testid="pageContainer">
      <div className={s.bgGray}>
        <div className={s.pageHeader}>
          <h2 data-aos="fade-up" data-testid="title">
            {title}
          </h2>
          <p data-aos="fade-up" data-aos-delay="100" data-testid="locationAndEmploymentType">
            {`${employmentType} • ${location}`}
          </p>
          <div data-aos="fade-up" data-aos-delay="200">
            <Link href="/careers">View All Jobs</Link>
          </div>
        </div>
      </div>
      <div className={s.pageContent}>
        <div
          data-aos="fade-up"
          data-aos-delay="300"
          className={s.description}
          data-testid="description"
        >
          {parse(parse(description).toString(), { trim: true })}
        </div>
        <div
          data-aos="fade-up"
          data-aos-delay="300"
          id="applicationForm"
          className={s.applicationForm}
          data-testid="applicationFormWrapper"
        >
          <div className={s.formHeader}>
            <h3>Apply for this job</h3>
          </div>
          <div className={s.questionsContainer}>
            <div className={s.bottomSeparator}>
              <div className={s.nameRow}>
                <TextField
                  dataTestId="FirstNameTextField"
                  label="First Name"
                  isRequired
                  value={formData.first_name || ''}
                  onChange={(value) => onTextInputChange(value, 'first_name')}
                  onBlur={(e) => onBlur(e.target.value, 'first_name', true)}
                  helperText="Required*"
                  isError={formErrors.first_name}
                  placeholder="first name"
                />
                <TextField
                  dataTestId="LastNameTextField"
                  label="Last Name"
                  isRequired
                  value={formData.last_name || ''}
                  onChange={(value) => onTextInputChange(value, 'last_name')}
                  onBlur={(e) => onBlur(e.target.value, 'last_name', true)}
                  helperText="Required*"
                  isError={formErrors.last_name}
                  placeholder="last name"
                />
              </div>
              <TextField
                dataTestId="EmailTextField"
                label="Email"
                isRequired
                value={formData.email || ''} 
                onChange={(value) => onTextInputChange(value, 'email')}
                onBlur={(e) => onBlur(e.target.value, 'email', true)}
                helperText="Required*"
                isError={formErrors.email}
                placeholder="name@email.com"
              />
              <TextField
                dataTestId="PhoneTextField"
                label="Phone"
                value={formData.phone || ''}
                onChange={(value) => onTextInputChange(value, 'phone')}
                onBlur={(e) => onBlur(e.target.value, 'phone', requiredFields?.phone)}
                helperText="Required*"
                isError={formErrors.phone}
                placeholder="+1 (234) 567 9999"
                isRequired={requiredFields?.phone}
              />
              <div className={s.InputFileContainer}>
                <div>
                  <p>{`Resume/CV${requiredFields?.resume ? ' *' : ''}`}</p>
                  <p className={s.caption}>(File types: pdf, doc, docx)</p>
                </div>
                <div className={s.buttonContainer}>
                  <Button dataTestId="ResumeUploadButton" onClick={handleResumeSubmit}>
                    <AttachmentPinIcon />
                    Select Resume
                  </Button>
                  {formData?.resume_content_filename && (
                    <p className={s.caption}>{formData?.resume_content_filename}</p>
                  )}
                </div>
                <input
                  className={s.fileInput}
                  type="file"
                  onChange={handleHiddenInputChange}
                  ref={hiddenFileInput}
                  accept=".doc,.docx,.pdf"
                />
              </div>
              {formErrors?.resume && <p className={s.helperText}>Required *</p>}
            </div>
            {educationRequired && degrees.length > 0 && (
              <div className={classNames(s.educationContainer, s.bottomSeparator)}>
                {educations.map((education, index) => (
                  <Select
                    dataTestId="Educations"
                    key={education.value}
                    value={education.value}
                    label="Degree *"
                    placeholder="Select degree"
                    items={degrees}
                    helperText="Required *"
                    isError={education?.error}
                    onChange={(e) => onEducationValueChange(e.target.value, index)}
                  />
                ))}
                <Button
                  onClick={() => {
                    setEducations((prev) => [...prev, { value: undefined }]);
                  }}
                >
                  + Add another education
                </Button>
              </div>
            )}
            <div className={s.customQuestions}>
              {questions.map((question) => {
                return getInputField(question);
              })}
            </div>
          </div>
          {!isFormSubmitting && (
            <Button variant="primary" dataTestId="SubmitButton" onClick={onFormSubmit}>
              Submit Application
            </Button>
          )}
          {isFormSubmitting && <LoadingSpinner />}
          {errorMessage && <p className={s.errorMessage}>{errorMessage}</p>}
          {successMessage && <p className={s.successMessage}>{successMessage}</p>}
        </div>
      </div>
    </div>
  );
};

export default JobDetails;
